import type { QueryClient } from "@tanstack/react-query";

import type { BaseEntity } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { DetailActionContext } from "@/components/shared/UniversalDetailPage/types/action-types";
import type { ResolverEntityKey } from "@/lib/actions/relationships/types";

import { readCompanyContactsFromCache } from "./readCompanyContactsFromCache";
import type { ActionLaunchHints } from "./types";

const RESOLVER_ENTITY_MAP: Record<string, ResolverEntityKey> = {
  contacts: "contact",
  companies: "company",
  deals: "deal",
  tickets: "ticket",
  communications: "communication",
  time_entries: "time_entry",
  tasks: "task",
  recurring_charges: "recurring_charge",
  recurring_transactions: "recurring_transaction",
};

type HintContext<T extends BaseEntity> = Pick<
  DetailActionContext<T>,
  "item" | "entityIds" | "pageConfig" | "surfaceEntityType" | "launchHints"
>;

/**
 * Derive launch hints from row-click context so the resolver does not re-fetch
 * data the UI already has (contact email on the row, company contacts in cache).
 */
export function buildActionLaunchHints<T extends BaseEntity>(
  actionId: string,
  context: HintContext<T>,
  queryClient?: QueryClient,
): ActionLaunchHints | undefined {
  if (context.launchHints?.prefetchedTargets?.length) {
    return context.launchHints;
  }

  if (actionId !== "send-email") {
    return undefined;
  }

  const entityName = context.pageConfig?.entityName ?? context.surfaceEntityType;
  const sourceEntity = entityName ? RESOLVER_ENTITY_MAP[entityName] : undefined;

  if (sourceEntity === "contact") {
    const item = context.item as { id?: string; email?: string | null; name?: string | null };
    const email = item.email?.trim();
    if (item.id && email) {
      return {
        prefetchedTargets: [
          {
            id: item.id,
            email,
            name: item.name?.trim() || undefined,
          },
        ],
      };
    }
  }

  if (sourceEntity === "company" && queryClient) {
    const companyId =
      context.entityIds.companyId ?? (context.item as { id?: string }).id;
    if (companyId) {
      const prefetchedTargets = readCompanyContactsFromCache(queryClient, companyId);
      if (prefetchedTargets?.length) {
        return { prefetchedTargets };
      }
    }
  }

  return undefined;
}
